import { uniq } from 'lodash';

import {
  ProfessionalTeamRoleKind,
  ProfessionalTeamRolePowerOfAttorneyKind,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import {
  ProfessionalTeamSlide_ClientOrganizationFragment,
  ProfessionalTeamSlide_ClientProfileFragment,
} from './graphql/ProfessionalTeamSlide.generated';
import {
  ADVISOR_CLIENT_TEAM_ORDER_LIST,
  PRINCIPAL_CLIENT_TEAM_ORDER_LIST,
  PROFESSIONAL_TEAM_COLUMN_LENGTH_NO_EMAIL,
  PROFESSIONAL_TEAM_COLUMN_LENGTH_WITH_EMAIL,
  PROFESSIONAL_TEAM_PAGE_LENGTH_NO_EMAIL,
  PROFESSIONAL_TEAM_PAGE_LENGTH_WITH_EMAIL,
} from './ProfessionalTeamSlide.constants';

export interface ProfessionalTeamSlideMember {
  name: string;
  principalClient: string | null;
  email: string | null;
  kind: ProfessionalTeamRoleKind;
  otherRoleName: string | null;
  powerOfAttorneyKind: ProfessionalTeamRolePowerOfAttorneyKind | null;
}

function getRolesForOrderList(
  inputList: ProfessionalTeamSlideInput[],
  orderList: ProfessionalTeamRoleKind[],
  principalClientId?: string
): ProfessionalTeamSlideMember[] {
  return orderList.reduce<ProfessionalTeamSlideMember[]>((acc, orderRole) => {
    inputList.forEach((profile) => {
      const roles = getNodes(profile.professionalTeamRoles);
      const role = roles.find((role) => {
        if (role.kind !== orderRole) {
          // exclude roles that don't match the current order role
          return false;
        }

        if (role.associatedClientIndividual) {
          // include the roles associated with the current principal client
          return role.associatedClientIndividual.id === principalClientId;
        } else if (principalClientId) {
          // exclude them, because they don't match the current principal client
          return false;
        }
        // case where there is no current principal client
        return true;
      });

      if (role) {
        acc.push({
          name: profile.displayName,
          email: profile.email || null,
          kind: orderRole,
          otherRoleName: role.otherRoleName || null,
          powerOfAttorneyKind: role.powerOfAttorneyKind || null,
          principalClient: role.associatedClientIndividual?.displayName || null,
        });
      }
    });
    return acc;
  }, []);
}

export function getPrincipalRoleIDs(
  inputList: ProfessionalTeamSlideInput[]
): string[] {
  const principalIds: string[] = uniq(
    inputList.flatMap((input) =>
      getNodes(input.professionalTeamRoles).flatMap((role) => {
        if (role.associatedClientIndividual) {
          return role.associatedClientIndividual?.id ?? [];
        }
        return [];
      })
    )
  );
  return principalIds;
}

export type ProfessionalTeamSlideInput =
  | ProfessionalTeamSlide_ClientProfileFragment
  | ProfessionalTeamSlide_ClientOrganizationFragment;

export function mapDataToTable(
  inputList: ProfessionalTeamSlideInput[]
): ProfessionalTeamSlideMember[] {
  // roles need to be displayed in a specific order, with the roles within specified by the associated list type:
  // 1. household-level roles
  const advisorClientRoles = getRolesForOrderList(
    inputList,
    ADVISOR_CLIENT_TEAM_ORDER_LIST
  );

  // 2. principal client-level roles (represented as an array here since there may be two principal clients)
  const principalIds: string[] = getPrincipalRoleIDs(inputList);
  const principalClientRoles = principalIds.map<ProfessionalTeamSlideMember[]>(
    (principalClientId) =>
      getRolesForOrderList(
        inputList,
        PRINCIPAL_CLIENT_TEAM_ORDER_LIST,
        principalClientId
      )
  );

  return advisorClientRoles.concat(...principalClientRoles);
}

/**
 * The number of members that fill a single column on a page. A page holding
 * anyone with an email gives each member a third line, so it fits fewer per
 * column than a page of only email-less members. The left column fills to
 * this count before any member spills into the right column.
 */
export function getProfessionalTeamColumnLength(
  page: ProfessionalTeamSlideMember[]
): number {
  const pageHasEmail = page.some((member) => !!member.email);
  return pageHasEmail
    ? PROFESSIONAL_TEAM_COLUMN_LENGTH_WITH_EMAIL
    : PROFESSIONAL_TEAM_COLUMN_LENGTH_NO_EMAIL;
}

/**
 * Split the professional team into slide-sized pages.
 *
 * A member with an email occupies a third line and so needs more vertical
 * room. The capacity is decided per page from the members actually on it: a
 * page that holds anyone with an email fits {@link
 * PROFESSIONAL_TEAM_PAGE_LENGTH_WITH_EMAIL}, while a page of only email-less
 * members fits the larger {@link PROFESSIONAL_TEAM_PAGE_LENGTH_NO_EMAIL}.
 */
export function paginateProfessionalTeam(
  professionalTeam: ProfessionalTeamSlideMember[]
): ProfessionalTeamSlideMember[][] {
  const pages: ProfessionalTeamSlideMember[][] = [];
  let currentPage: ProfessionalTeamSlideMember[] = [];
  let currentPageHasEmail = false;

  for (const member of professionalTeam) {
    // The member we're about to place decides whether this page now contains
    // an email line, which in turn sets the page's capacity.
    const pageWouldHaveEmail = currentPageHasEmail || !!member.email;
    const capacity = pageWouldHaveEmail
      ? PROFESSIONAL_TEAM_PAGE_LENGTH_WITH_EMAIL
      : PROFESSIONAL_TEAM_PAGE_LENGTH_NO_EMAIL;

    if (currentPage.length >= capacity) {
      pages.push(currentPage);
      currentPage = [];
      currentPageHasEmail = false;
    }

    currentPage.push(member);
    currentPageHasEmail = currentPageHasEmail || !!member.email;
  }

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return pages;
}
